import { createSelector } from "reselect";
import { getPersonalizedDateFormat, getSortNotesConfigOption } from "~/store/selectors/selector.configOptions";
import { formatCreationDate } from "~/utils/date";
import { descSortCompare, sortCompare } from "~/utils/utils";

export const getAllNotes = (state) => state.notes;
export const getNoteById = createSelector(
  getAllNotes,
  (state, id) => id,
  (notes, id) => notes[id]
);

export const getNotesByProjectAndByNotebook = createSelector(
  getAllNotes,
  (state, projectId) => projectId,
  (state, projectId, notebookId) => notebookId,
  (notes, projectId, notebookId = 0) => {
    return Object.values(notes).filter(
      (note) => note.project_id === projectId && !note.is_trashed && note.notebook_id === notebookId
    );
  }
);

const sortNotesForScreen = (notes, sortConfig) => {
  let sorted = [];

  if (sortConfig.sort === "created") {
    sorted = notes.sort((a, b) => {
      if (sortConfig.order === "desc") {
        return descSortCompare(a.created_on, b.created_on);
      }

      return sortCompare(a.created_on, b.created_on);
    });
  } else if (sortConfig.sort === "name") {
    sorted = notes.sort((a, b) => {
      let dir = 1;
      if (sortConfig.order === "desc") {
        dir = -1;
      }
      const first = a.name.replace(
        /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
        "Z"
      );
      const second = b.name.replace(
        /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
        "Z"
      );
      return dir * first.localeCompare(second);
    });
  } else {
    sorted = notes.sort((a, b) => {
      if (sortConfig.order === "desc") {
        if (a.updated_on === b.updated_on) {
          return a.position < b.position ? -1 : 1;
        }
        return descSortCompare(a.updated_on, b.updated_on);
      }
      if (a.updated_on === b.updated_on) {
        return a.position < b.position ? 1 : -1;
      }
      return sortCompare(a.updated_on, b.updated_on);
    });
  }

  return sorted;
};

export const getNotesGroupedCollection = createSelector(
  getNotesByProjectAndByNotebook,
  getSortNotesConfigOption,
  getPersonalizedDateFormat,
  (notes, sortConfig, format) => {
    const sorted = sortNotesForScreen(notes, sortConfig);
    const collection = { "#pinned": [], "#other": [] };

    const result = sorted.reduce((prev, next) => {
      let key;

      if (next.is_pinned) {
        key = "#pinned";
      } else if (sortConfig.sort === "name") {
        key = "#other";
      } else if (sortConfig.sort === "updated") {
        key = formatCreationDate(next.updated_on, format, false);
      } else {
        key = formatCreationDate(next.created_on, format, false);
      }

      if (!prev[key]) {
        prev[key] = [];
      }

      prev[key].push(next.id);

      return prev;
    }, collection);

    return Object.keys(result).reduce((prev, next) => {
      if (result[next].length) {
        prev[next] = result[next];
      }

      return prev;
    }, {});
  }
);
